/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import type { MutableRefObject } from 'react';
import { useState } from 'react';

import type {
	EgressDeleteDomainPayload,
	EgressDeleteGroupPayload,
	EgressGetPayload,
	EgressGetResponse,
	EgressSetPayload,
	RemoteGetPayload,
	RemoteSetPayload,
} from './types';
import { BridgeClientError, type BridgeConfig } from '../..';
import { AdminModal } from './AdminConsentModal';
import { fg } from '@atlaskit/platform-feature-flags';
import { usePermissionFetchClient } from './usePermissionFetchClient';
import type { MutationHookOptions } from '@apollo/react-hooks';
import type { ContextId, ExtensionId } from '@atlassian/forge-ui-types';
import type { MutationData, MutationVariables } from '../useProductFetchClient';
import type { ApiRefType } from '../../useBridge';
import { createBridgeMethod } from '../../createBridgeApi';
import { useForgeAppQuery } from '../useForgeAppQuery';
import { jsx } from '@compiled/react';

export interface usePermissionProps {
	accountType?: 'atlassian' | 'customer' | 'anonymous';
	appId: string;
	contextIds: ContextId[];
	extensionId: ExtensionId;
	installationId: string;
	options?: MutationHookOptions<MutationData, MutationVariables>;
	renderThreeLOPrompt: (retry: () => Promise<void>) => Promise<void>;
}

export type ManifestRemoteMap = Record<
	string,
	| {
			description: string;
			name: string;
			supportedPatterns: string[];
	  }
	| undefined
>;

type DomainInfoEgress = {
	payload: EgressSetPayload;
	type: 'EGRESS';
};

type DomainInfoRemote = {
	payload: RemoteSetPayload;
	manifestRemoteMap: ManifestRemoteMap;
	type: 'REMOTE';
};

export type PermissionBridgeMethods = {
	__permission__egressDeleteDomain?: (payload: any) => void;
	__permission__egressDeleteGroup?: (payload: any) => void;
	__permission__egressGet?: (payload?: any) => void;
	__permission__egressSet?: (payload?: any) => void;
	__permission__remoteGet?: (payload: any) => void;
	__permission__remoteSet?: (payload: any) => void;
};

export type ModalState =
	| { state: 'closed' }
	| {
			appName: string;
			domainInfo: DomainInfoEgress | DomainInfoRemote;
			onCancel: () => void;
			onConfirm: () => void;
			privacyPolicyLink?: string | null;
			state: 'open';
			termsOfUseLink?: string | null;
	  };

type PermissionMethod =
	| 'egressSet'
	| 'egressGet'
	| 'egressDeleteDomain'
	| 'egressDeleteGroup'
	| 'remoteGet'
	| 'remoteSet';

export const permissionMethods: PermissionMethod[] = [
	'egressSet',
	'egressGet',
	'egressDeleteDomain',
	'egressDeleteGroup',
	'remoteGet',
	'remoteSet',
];

export const PERMISSION_METHOD_PREFIX = '__permission__';

export const getUseBridgePermissionFunctions = (apiRef: MutableRefObject<ApiRefType>) => ({
	__permission__egressSet: async ({ data }: { data: any }) => {
		return apiRef.current.__permission__egressSet?.(data);
	},
	__permission__egressGet: async ({ data }: { data: any }) => {
		return apiRef.current.__permission__egressGet?.(data);
	},
	__permission__egressDeleteDomain: async ({ data }: { data: any }) => {
		return apiRef.current.__permission__egressDeleteDomain?.(data);
	},
	__permission__egressDeleteGroup: async ({ data }: { data: any }) => {
		return apiRef.current.__permission__egressDeleteGroup?.(data);
	},
	__permission__remoteGet: async ({ data }: { data: any }) => {
		return apiRef.current.__permission__remoteGet?.(data);
	},
	__permission__remoteSet: async ({ data }: { data: any }) => {
		return apiRef.current.__permission__remoteSet?.(data);
	},
});

export const getCreateBridgeApiPermissionFunctions = (
	bridge: BridgeConfig | undefined,
): PermissionBridgeMethods =>
	Object.fromEntries(
		permissionMethods.map((method) => [
			`${PERMISSION_METHOD_PREFIX}${method}`,
			createBridgeMethod(bridge?.[`${PERMISSION_METHOD_PREFIX}${method}`]),
		]),
	);

export function usePermission({
	appId,
	installationId,
	contextIds,
	extensionId,
	options,
	renderThreeLOPrompt,
}: usePermissionProps) {
	const [modalState, setModalState] = useState<ModalState>({ state: 'closed' });

	const permissionFetchClient = usePermissionFetchClient({
		appId,
		installationId,
		contextIds,
		extensionId,
		options,
		renderThreeLOPrompt,
	});

	if (!options?.client) {
		throw new BridgeClientError(`GraphQL client has not been initialized`);
	}

	const queryForgeApp = useForgeAppQuery({
		client: options.client,
	});

	const getAllCurrentEgress = async () => {
		const getEgress = await permissionFetchClient.egressGet({});

		return (await getEgress.json()) as Promise<EgressGetResponse>;
	};

	const shouldDisplayModalForEgress = async (payload: EgressSetPayload) => {
		const allCurrentEgressResp = await getAllCurrentEgress();
		const currentDomainMap: { [key: string]: boolean } = (
			allCurrentEgressResp?.results ?? []
		).reduce(
			(acc, result) => {
				result.configured.forEach((d) => (acc[d.domain] = true));

				return acc;
			},
			{} as { [key: string]: boolean },
		);

		const domains = payload.egresses.reduce((acc, group) => {
			const urls = group.configured.map((c) => c.domain);

			acc.push(...urls);
			return acc;
		}, [] as string[]);

		return domains.some((d) => !currentDomainMap[d]);
	};

	const fetchAppData = async (id: string, contextIds: string[]) => {
		try {
			return await queryForgeApp(id, contextIds);
		} catch (e) {
			if (e instanceof Error) {
				const error = new BridgeClientError(e.message);
				error.stack = e.stack;
				throw error;
			}
			throw new BridgeClientError(typeof e === 'string' ? e : String(e));
		}
	};

	return {
		shouldDisplayModalForEgress,
		getModalJsx: (): JSX.Element | null =>
			fg('configurable-egress-mvp-frontend') ? <AdminModal modalState={modalState} /> : null,
		egressSet: async (payload: EgressSetPayload) => {
			if (!fg('configurable-egress-mvp-frontend')) {
				return {};
			}
			const app = await fetchAppData(appId, contextIds);
			const response = await permissionFetchClient.egressSet(payload, true);

			if (!response.ok) {
				throw new BridgeClientError('Dry run failed');
			}

			try {
				const shouldDisplay = await shouldDisplayModalForEgress(payload);
				if (shouldDisplay) {
					return new Promise<void>((resolve, reject) => {
						setModalState({
							state: 'open',
							onCancel: () => {
								setModalState({ state: 'closed' });
								reject('Setting egress was rejected by admin');
							},
							onConfirm: async () => {
								try {
									const setEgressResp = await permissionFetchClient.egressSet(payload, false);
									const json = await setEgressResp.json();

									if (!setEgressResp.ok) {
										throw new BridgeClientError(JSON.stringify(json, null, 2));
									}

									setModalState({ state: 'closed' });
									resolve(json);
								} catch (err) {
									reject(err);
								}
							},
							domainInfo: { type: 'EGRESS', payload },
							appName: app.name,
							termsOfUseLink: app.termsOfService,
							privacyPolicyLink: app.privacyPolicy,
						});
					});
				} else {
					const setEgressResp = await permissionFetchClient.egressSet(payload, false);
					const json = await setEgressResp.json();

					if (!setEgressResp.ok) {
						throw new BridgeClientError(JSON.stringify(json, null, 2));
					}

					return json;
				}
			} catch (err) {
				throw new BridgeClientError(`Could not set egress due to ${err}`);
			}
		},
		egressGet: async (payload: EgressGetPayload) => {
			if (!fg('configurable-egress-mvp-frontend')) {
				return {};
			}

			try {
				const getEgressResp = await permissionFetchClient.egressGet(payload);
				const json = await getEgressResp.json();

				if (!getEgressResp.ok) {
					throw new BridgeClientError(JSON.stringify(json, null, 2));
				}

				return json;
			} catch (err) {
				throw new BridgeClientError(`Could not get egress due to: ${err}`);
			}
		},
		egressDeleteDomain: async (payload: EgressDeleteDomainPayload) => {
			if (!fg('configurable-egress-mvp-frontend')) {
				return {};
			}

			try {
				const egressDeleteDomainResp = await permissionFetchClient.egressDeleteDomain(payload);
				const json = await egressDeleteDomainResp.json();

				if (!egressDeleteDomainResp.ok) {
					throw new BridgeClientError(JSON.stringify(json, null, 2));
				}

				return json;
			} catch (err) {
				throw new BridgeClientError(`Could not delete egress domain due to ${err}`);
			}
		},
		egressDeleteGroup: async (payload: EgressDeleteGroupPayload) => {
			if (!fg('configurable-egress-mvp-frontend')) {
				return {};
			}

			try {
				const egressDeleteGroupResp = await permissionFetchClient.egressDeleteGroup(payload);
				const json = await egressDeleteGroupResp.json();

				if (!egressDeleteGroupResp.ok) {
					throw new BridgeClientError(JSON.stringify(json, null, 2));
				}

				return json;
			} catch (err) {
				throw new BridgeClientError(`Could not delete egress group due to ${err}`);
			}
		},
		remoteGet: async (payload: RemoteGetPayload) => {
			if (!fg('configurable-egress-mvp-frontend')) {
				return {};
			}

			try {
				const remoteGetResp = await permissionFetchClient.remoteGet(payload);
				const json = await remoteGetResp.json();

				if (!remoteGetResp.ok) {
					throw new BridgeClientError(JSON.stringify(json, null, 2));
				}

				return json;
			} catch (err) {
				throw new BridgeClientError(`Could not get remote due to: ${err}`);
			}
		},
		remoteSet: async (payload: RemoteSetPayload) => {
			if (!fg('configurable-egress-mvp-frontend')) {
				return {};
			}
			const app = await fetchAppData(appId, contextIds);

			const response = await permissionFetchClient.remoteSet(payload, true);

			if (!response.ok) {
				throw new BridgeClientError('Dry run failed');
			}

			const node = (app.installationsByContexts?.edges ?? [])[0]?.node;
			const payloadKeys = new Set(payload.remotes.map((r) => r.key));
			const manifestRemoteMap = (node?.appEnvironmentVersion?.extensions?.nodes ?? [])
				.filter(
					(e) =>
						e && e.extensionData.type === 'core:remote' && payloadKeys.has(e.extensionData.key),
				)
				.reduce((acc, e) => {
					if (e?.extensionData) {
						acc[e.extensionData.key] = e.extensionData.configurable;
					}
					return acc;
				}, {} as ManifestRemoteMap);

			return new Promise<void>((resolve, reject) => {
				setModalState({
					state: 'open',
					onCancel: () => {
						setModalState({ state: 'closed' });
						reject(new BridgeClientError('Setting remote was rejected by admin'));
					},
					onConfirm: async () => {
						try {
							const remoteSetResp = await permissionFetchClient.remoteSet(payload, false);

							if (!remoteSetResp.ok) {
								throw new BridgeClientError(remoteSetResp.statusText);
							}

							setModalState({ state: 'closed' });
							resolve();
						} catch (err) {
							reject(
								new BridgeClientError(`API call was rejected with the following error: ${err}`),
							);
						}
					},
					domainInfo: {
						type: 'REMOTE',
						payload,
						manifestRemoteMap,
					},
					appName: app.name,
					termsOfUseLink: app.termsOfService,
					privacyPolicyLink: app.privacyPolicy,
				});
			});
		},
	};
}
